import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from '../hooks/useTranslation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { useCheckout, CheckoutSession } from '../contexts/CheckoutContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
  textAlign: 'center',
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 80,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

export const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { user } = useAuth();
  const { refreshPurchaseHistory, fetchCheckoutSession } = useCheckout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingMessage, setProcessingMessage] = useState(t('payment.processing.title') || 'Processing your payment...');

  useEffect(() => {
    // Get payment ID from URL parameters
    const paymentIdParam = searchParams.get('payment_id');
    setPaymentId(paymentIdParam);

    // Start processing monitoring
    startProcessingMonitoring();
  }, [searchParams]);

  const startProcessingMonitoring = async () => {
    // Check every 2 seconds if orders have been created and checkout is cleared
    const checkInterval = setInterval(async () => {
      try {
        // Refresh both purchase history and checkout session
        await Promise.all([
          refreshPurchaseHistory(),
          fetchCheckoutSession()
        ]);
        
        // Check checkout session directly to see if it's empty
        const checkoutResponse = await api.get<{ data: CheckoutSession }>(`/checkout-sessions/user/${user?.id}`, {
          params: { includePrice: true }
        });
        const currentCheckoutItems = checkoutResponse.data.data?.items || [];
        
        // Check if checkout is empty (indicating webhook has processed)
        if (currentCheckoutItems.length === 0) {
          setProcessingMessage(t('payment.success.title') || 'Payment processed successfully!');
          setIsProcessing(false);
          clearInterval(checkInterval);
        } else {
          setProcessingMessage(t('payment.processing.creatingOrders') || 'Creating your orders...');
        }
      } catch (error) {
        console.error('Error checking processing status:', error);
      }
    }, 2000);

    // Timeout after 30 seconds to prevent infinite waiting
    setTimeout(() => {
      setProcessingMessage(t('payment.processing.completed') || 'Processing completed');
      setIsProcessing(false);
      clearInterval(checkInterval);
    }, 30000);
  };

  const handleContinueShopping = () => {
    navigate('/');
  };

  const handleViewOrders = () => {
    navigate('/my-orders');
  };

  if (isProcessing) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <StyledPaper>
          <Box sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress size={60} sx={{ color: 'primary.main' }} />
            </Box>
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('payment.processing.title') || 'Processing Your Payment...'}
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {processingMessage}
            </Typography>

            {paymentId && (
              <Box sx={{ mb: 3 }}>
                <Chip 
                  label={`Payment ID: ${paymentId}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '0.875rem' }}
                />
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t('payment.processing.details') || 'Please wait while we create your orders and process your payment. This may take a few moments.'}
            </Typography>
          </Box>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <StyledPaper>
        <Box sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
          <SuccessIcon />
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {t('payment.success.title') || 'Payment Successful!'}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('payment.success.message') || 'Thank you for your purchase. Your payment has been processed successfully.'}
          </Typography>

          {paymentId && (
            <Box sx={{ mb: 3 }}>
              <Chip 
                label={`Payment ID: ${paymentId}`} 
                color="primary" 
                variant="outlined"
                sx={{ fontSize: '0.875rem' }}
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('payment.success.details') || 'You will receive a confirmation email shortly with your purchase details.'}
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 3, 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Button
              variant="outlined"
              onClick={handleViewOrders}
              sx={{ borderRadius: 2 }}
            >
              {t('payment.success.viewOrders') || 'View My Orders'}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinueShopping}
              sx={{ borderRadius: 2 }}
            >
              {t('payment.success.continueShopping') || 'Continue Shopping'}
            </Button>
          </Box>
        </Box>
      </StyledPaper>
    </Container>
  );
}; 